<template>
    <div>
        <b-alert variant="warning" show class="mb-3">
            <i class="bi bi-exclamation-triangle-fill mr-1"></i>
            <strong>되돌릴 수 없는 작업입니다.</strong>
            변경 후 캠페인에 <u>없는 문항</u>의 답변과 <u>감사자 평가 전량</u>은 영구 삭제됩니다.
            삭제된 원본은 실행 이력에 백업되지만, <strong>복구는 자동으로 되지 않습니다</strong>
            (담당자가 직접 복원해야 합니다).
            반드시 미리보기로 삭제 건수를 확인하고 관계자 동의를 받은 뒤 실행하세요.
        </b-alert>

        <!-- ── 대상 선택 ── -->
        <b-card class="mb-3">
            <b-row>
                <b-col md="3">
                    <b-form-group label="기간" class="mb-2">
                        <b-form-select v-model="selectedPeriod" :disabled="isBusy" @change="onPeriodChange">
                            <b-form-select-option v-for="p in periods" :key="p" :value="p">
                                {{ p }}
                            </b-form-select-option>
                        </b-form-select>
                    </b-form-group>
                </b-col>
                <b-col md="5">
                    <b-form-group label="프로젝트" class="mb-2">
                        <auto-complete-select v-model="selectedProjectCode"
                                              :options="projectOptions"
                                              option-value-key="id"
                                              :loading="loadingProjects"
                                              :disabled="isBusy"
                                              :placeholder="projectPlaceholder"
                                              list-max-height="360px"
                                              clearable
                                              @input="onProjectChange"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group label="업체 (현재 캠페인)" class="mb-2">
                        <!-- 한 프로젝트에 업체가 수백 개일 수 있어 검색 드롭다운을 쓴다.
                             업체명과 company_id 를 모두 text 에 담아 둘 다로 검색되게 한다. -->
                        <auto-complete-select v-model="selectedCompanyKey"
                                              :options="companyOptions"
                                              option-value-key="id"
                                              :loading="loadingCompanies"
                                              :disabled="isBusy || !selectedProjectCode"
                                              :placeholder="companyPlaceholder"
                                              list-max-height="360px"
                                              clearable
                                              @input="onCompanyChange"
                        />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row class="align-items-end">
                <b-col md="4">
                    <b-form-group label="변경 전 캠페인" class="mb-0">
                        <b-form-input :value="fromCampaignText" disabled/>
                    </b-form-group>
                </b-col>
                <b-col md="1" class="text-center pb-2 d-none d-md-block">
                    <i class="bi bi-arrow-right" style="font-size: 1.4rem;"></i>
                </b-col>
                <b-col md="4">
                    <b-form-group label="변경 후 캠페인" class="mb-0">
                        <b-form-select v-model="toCampaignId"
                                       :disabled="isBusy || !selectedCompany || loadingCampaigns"
                                       @change="resetPreview"
                        >
                            <b-form-select-option :value="null" disabled>
                                {{ toCampaignPlaceholder }}
                            </b-form-select-option>
                            <b-form-select-option v-for="c in toCampaignOptions"
                                                  :key="c.id" :value="c.id"
                            >
                                {{ c.campaign_name }}
                            </b-form-select-option>
                        </b-form-select>
                    </b-form-group>
                </b-col>
                <b-col md="3" class="text-right">
                    <b-button variant="outline-primary" :disabled="!canPreview || isBusy"
                              @click="doPreview"
                    >
                        <b-spinner v-if="previewLoading" small class="mr-1"/>
                        미리보기
                    </b-button>
                </b-col>
            </b-row>
        </b-card>

        <!-- ── 미리보기 결과 ── -->
        <template v-if="preview">
            <b-alert v-for="(w, i) in preview.warnings" :key="`w-${i}`"
                     variant="warning" show class="py-2"
            >
                <i class="bi bi-exclamation-circle mr-1"></i>{{ w }}
            </b-alert>

            <b-card class="mb-3">
                <h5 class="mb-3">
                    영향 범위
                    <small class="text-muted ml-2">
                        {{ preview.from_campaign_name }} &rarr; {{ preview.to_campaign_name }}
                    </small>
                </h5>

                <b-row class="text-center mb-3">
                    <b-col>
                        <div class="summary-box">
                            <div class="summary-label">현재 답변</div>
                            <div class="summary-value">{{ preview.at_from }}</div>
                        </div>
                    </b-col>
                    <b-col>
                        <div class="summary-box border-success">
                            <div class="summary-label">유지</div>
                            <div class="summary-value text-success">{{ preview.keep_count }}</div>
                        </div>
                    </b-col>
                    <b-col>
                        <div class="summary-box" :class="preview.delete_count ? 'border-danger' : ''">
                            <div class="summary-label">영구 삭제</div>
                            <div class="summary-value" :class="preview.delete_count ? 'text-danger' : ''">
                                {{ preview.delete_count }}
                            </div>
                        </div>
                    </b-col>
                    <b-col>
                        <div class="summary-box"
                             :class="preview.evaluation_delete_count ? 'border-danger' : ''"
                        >
                            <div class="summary-label">감사자 평가 삭제</div>
                            <div class="summary-value"
                                 :class="preview.evaluation_delete_count ? 'text-danger' : ''"
                            >
                                {{ preview.evaluation_delete_count }}
                            </div>
                        </div>
                    </b-col>
                    <b-col>
                        <div class="summary-box">
                            <div class="summary-label">첨부</div>
                            <div class="summary-value">{{ preview.attachment_storage_count }}</div>
                        </div>
                    </b-col>
                </b-row>

                <b-alert v-if="preview.evaluation_delete_count" variant="danger" show class="py-2">
                    <i class="bi bi-x-octagon-fill mr-1"></i>
                    감사자가 이미 매긴 평가 <strong>{{ preview.evaluation_delete_count }}건</strong>이
                    삭제됩니다. 재매핑이 불가능한 구조라 감사자가 <strong>재평가</strong>해야 합니다 —
                    실행 전 반드시 동의를 받으세요.
                </b-alert>

                <!-- 삭제될 문항 목록 -->
                <div v-if="preview.delete_sub_codes && preview.delete_sub_codes.length">
                    <b-button variant="link" class="p-0 mb-2" @click="showDeleteList = !showDeleteList">
                        <i class="bi" :class="showDeleteList ? 'bi-caret-down-fill' : 'bi-caret-right-fill'"></i>
                        삭제될 문항 {{ preview.delete_sub_codes.length }}개 보기
                    </b-button>
                    <b-collapse v-model="showDeleteList">
                        <div class="delete-list p-2 mb-2">
                            <b-badge v-for="code in preview.delete_sub_codes" :key="code"
                                     variant="light" class="mr-1 mb-1 border"
                            >
                                {{ code }}
                            </b-badge>
                        </div>
                        <p class="text-muted small mb-0">
                            변경 후 캠페인에 없는 문항입니다. 이 문항들의 답변은 삭제되며,
                            원본은 실행 이력에 백업됩니다.
                        </p>
                    </b-collapse>
                </div>
                <p v-else class="text-success mb-0">
                    <i class="bi bi-check-circle mr-1"></i>삭제되는 답변이 없습니다 — 전부 유지됩니다.
                </p>
            </b-card>

            <b-card class="mb-3">
                <h6 class="mb-2">함께 이동하는 배정</h6>
                <p class="small text-muted">
                    답변·멤버십은 (업체, 캠페인) 단위로 1벌이라 프로젝트별로 나눌 수 없습니다.
                    아래 프로젝트의 배정이 <strong>함께</strong> 이동합니다.
                </p>
                <b-badge v-for="p in preview.affected_project_codes" :key="p"
                         variant="primary" class="mr-1"
                >
                    {{ p || '(프로젝트 없음)' }}
                </b-badge>
                <b-table v-if="preview.nsr_rows && preview.nsr_rows.length"
                         :items="preview.nsr_rows" :fields="nsrFields" small striped
                         responsive class="mt-2 mb-0"
                />
            </b-card>

            <div class="text-right mb-4">
                <b-button variant="link" :disabled="isBusy" @click="resetAll">초기화</b-button>
                <b-button variant="danger" :disabled="isBusy" @click="doExecute">
                    <b-spinner v-if="executing" small class="mr-1"/>
                    재배정 실행
                </b-button>
            </div>
        </template>

        <!-- ── 실행 결과 ── -->
        <b-card v-if="result" border-variant="success" class="mb-3">
            <h5 class="text-success mb-3">
                <i class="bi bi-check-circle-fill mr-1"></i>재배정 완료
                <small class="text-muted ml-2">이력 #{{ result.log_id }}</small>
            </h5>
            <b-row class="mb-3">
                <b-col md="4">유지된 답변: <strong>{{ result.keep_count }}</strong></b-col>
                <b-col md="4">삭제된 답변: <strong>{{ result.delete_count }}</strong></b-col>
                <b-col md="4">삭제된 평가: <strong>{{ result.evaluation_delete_count }}</strong></b-col>
            </b-row>
            <b-button size="sm" variant="outline-secondary" @click="$emit('go-history', result.log_id)">
                실행 이력에서 백업 확인
            </b-button>
        </b-card>
    </div>
</template>

<script>
/**
 * 답변 보존 캠페인 재배정 — 실행 탭.
 *
 * ⛔ 이 화면의 규율: preview 응답의 execute_requires 를 **그대로** execute 로 넘긴다.
 *    삭제 건수·영향 프로젝트를 화면에서 다시 계산하지 않는다. 서버가 자기 계산과
 *    대조해 불일치 시 거절하는데, 화면이 임의로 만든 값을 보내면 "확인했다"는 신호가
 *    거짓이 되기 때문이다(그 확인이 유일한 오조작 방지선이다).
 */
import AutoCompleteSelect from '@core/components/common/AutoCompleteSelect.vue';
import {
    executeRemap,
    previewRemap,
    remapErrorMessage,
} from '@shared_utils/api/campaignRemap';

export default {
    name: 'RemapExecuteTab',
    components: {AutoCompleteSelect},
    data() {
        return {
            periods: ['2024_1H', '2025_1H', '2026_1H'],
            selectedPeriod: '2026_1H',
            projects: [],
            selectedProjectCode: null,
            companies: [],
            selectedCompanyKey: null,
            campaigns: [],
            toCampaignId: null,
            preview: null,
            result: null,
            showDeleteList: false,
            loadingProjects: false,
            loadingCompanies: false,
            loadingCampaigns: false,
            previewLoading: false,
            executing: false,
            nsrFields: [
                {key: 'campaign_id', label: '캠페인 ID'},
                {key: 'project_code', label: '프로젝트'},
                {key: 'status_code', label: '상태'},
            ],
        };
    },
    computed: {
        isBusy() {
            return this.previewLoading || this.executing;
        },
        /** auto-complete-select 규약: {id, text}. 검색 필터는 text 만 본다. */
        projectOptions() {
            const seen = new Set();
            const out = [];
            for (const group of this.projects || []) {
                for (const p of (group.projects || [group])) {
                    const code = p.project_code;
                    if (!code || seen.has(code)) continue;
                    seen.add(code);
                    const name = p.project_name || p.name || '';
                    out.push({
                        id: code,
                        // 코드로도 이름으로도 검색되게 둘 다 담는다.
                        text: name && name !== code ? `${name} (${code})` : code,
                        // 정렬 전용 — 옵션 규약({id,text}) 밖 필드라 표시에는 안 쓰인다.
                        sortKey: Number(p.id) || 0,
                    });
                }
            }
            // 최신(=id 큰 것)이 위로. common-data.getProjectCodeOptions 와 같은 규칙.
            return out.sort((a, b) => b.sortKey - a.sortKey);
        },
        companyOptions() {
            return (this.companies || [])
                .filter(c => c.campaign_id && c.company_id)
                .map(c => ({
                    // 같은 업체가 캠페인별로 여러 행일 수 있어 복합키를 쓴다.
                    id: `${c.campaign_id}-${c.company_id}`,
                    // ⛔ 업체명과 company_id 를 함께 담아야 둘 다로 검색된다(필터가
                    //    text 만 보기 때문). 업체가 수백 개인 프로젝트에서 이게 핵심.
                    text: `${c.company_name || ''} (${c.company_id})`
                        + (c.campaign_name ? ` — ${c.campaign_name}` : ''),
                    campaign_id: c.campaign_id,
                    company_id: c.company_id,
                    campaign_name: c.campaign_name || '',
                    company_name: c.company_name || '',
                }))
                .sort((a, b) => a.text.localeCompare(b.text));
        },
        selectedProject() {
            return (this.projectOptions || []).find(p => p.id === this.selectedProjectCode) || null;
        },
        selectedCompany() {
            return (this.companyOptions || []).find(c => c.id === this.selectedCompanyKey) || null;
        },
        toCampaignOptions() {
            // 현재 캠페인은 대상에서 제외 (from == to 는 서버가 422 로 거절한다)
            const fromId = this.selectedCompany && this.selectedCompany.campaign_id;
            // 최신(=id 큰 것)이 위로. filter 가 새 배열을 주므로 sort 가 원본을 안 건드린다.
            return (this.campaigns || [])
                .filter(c => c.id && c.id !== fromId)
                .sort((a, b) => b.id - a.id);
        },
        fromCampaignText() {
            if (!this.selectedCompany) return '';
            const {campaign_name: name, campaign_id: id} = this.selectedCompany;
            return name ? `${name} (${id})` : `${id}`;
        },
        projectPlaceholder() {
            if (this.loadingProjects) return '불러오는 중...';
            return this.projectOptions.length ? '프로젝트를 선택하세요' : '프로젝트가 없습니다';
        },
        companyPlaceholder() {
            if (this.loadingCompanies) return '불러오는 중...';
            if (!this.selectedProjectCode) return '프로젝트를 먼저 선택하세요';
            return this.companyOptions.length ? '업체를 선택하세요' : '업체가 없습니다';
        },
        toCampaignPlaceholder() {
            if (this.loadingCampaigns) return '불러오는 중...';
            if (!this.selectedCompany) return '업체를 먼저 선택하세요';
            return this.toCampaignOptions.length ? '변경할 캠페인을 선택하세요' : '캠페인이 없습니다';
        },
        canPreview() {
            return !!(this.selectedCompany && this.toCampaignId);
        },
    },
    mounted() {
        this.fetchProjects();
        this.fetchCampaigns();
    },
    methods: {
        async fetchProjects() {
            this.loadingProjects = true;
            try {
                // /projects 는 per_page 상한(<=100)이 있어 페이지를 끝까지 수집한다.
                const PER_PAGE = 100;
                const items = [];
                let page = 1;
                let pages = 1;
                let total = Infinity;
                for (;;) {
                    const {data} = await this.$axios.get('/projects', {
                        params: {period: this.selectedPeriod, page, per_page: PER_PAGE},
                    });
                    const pageItems = (data && data.items) || [];
                    items.push(...pageItems);
                    pages = (data && data.pages) || page;
                    if (data && typeof data.total === 'number') total = data.total;
                    if (pageItems.length === 0 || items.length >= total || page >= pages) break;
                    page += 1;
                }
                this.projects = items;
            } catch (e) {
                this.$EventBus.$emit('toast-failure', '조회 오류', '프로젝트 목록을 불러오지 못했습니다.');
            } finally {
                this.loadingProjects = false;
            }
        },
        async fetchCampaigns() {
            this.loadingCampaigns = true;
            try {
                const {data} = await this.$axios.get('/campaigns', {
                    params: {period: this.selectedPeriod},
                });
                this.campaigns = (data && data.campaigns) || [];
            } catch (e) {
                this.$EventBus.$emit('toast-failure', '조회 오류', '캠페인 목록을 불러오지 못했습니다.');
            } finally {
                this.loadingCampaigns = false;
            }
        },
        onPeriodChange() {
            this.selectedProjectCode = null;
            this.companies = [];
            this.selectedCompanyKey = null;
            this.toCampaignId = null;
            this.resetPreview();
            this.fetchProjects();
            this.fetchCampaigns();
        },
        async onProjectChange() {
            this.selectedCompanyKey = null;
            this.companies = [];
            this.toCampaignId = null;
            this.resetPreview();
            if (!this.selectedProjectCode) return;
            this.loadingCompanies = true;
            try {
                const {data} = await this.$axios.get('/assessments/results', {
                    params: {project_code: this.selectedProjectCode},
                });
                this.companies = Array.isArray(data) ? data : [];
            } catch (e) {
                this.$EventBus.$emit('toast-failure', '조회 오류', '업체 목록을 불러오지 못했습니다.');
            } finally {
                this.loadingCompanies = false;
            }
        },
        onCompanyChange() {
            this.toCampaignId = null;
            this.resetPreview();
        },
        resetPreview() {
            this.preview = null;
            this.result = null;
            this.showDeleteList = false;
        },
        resetAll() {
            this.resetPreview();
            this.toCampaignId = null;
        },
        async doPreview() {
            if (!this.canPreview) return;
            this.previewLoading = true;
            this.result = null;
            try {
                this.preview = await previewRemap({
                    companyId: this.selectedCompany.company_id,
                    fromCampaignId: this.selectedCompany.campaign_id,
                    toCampaignId: this.toCampaignId,
                });
            } catch (e) {
                this.preview = null;
                this.$EventBus.$emit(
                    'toast-failure', '미리보기 실패',
                    remapErrorMessage(e, '미리보기를 가져오지 못했습니다.'),
                );
            } finally {
                this.previewLoading = false;
            }
        },
        async doExecute() {
            if (!this.preview) return;
            const p = this.preview;
            // ⛔ 줄바꿈(\n)은 렌더되지 않는다(white-space 클래스가 프로젝트에 없음).
            //    기존 확인 모달 관례대로 한 문단으로 잇되, 삭제 건수는 반드시 포함한다.
            const message = `${this.selectedCompany.text} 의 캠페인을 `
                + `${p.from_campaign_name} 에서 ${p.to_campaign_name} 으로 변경합니다. `
                + `답변 ${p.keep_count}건은 유지되고, `
                + `${p.delete_count}건은 영구 삭제됩니다`
                + (p.evaluation_delete_count
                    ? ` (감사자 평가 ${p.evaluation_delete_count}건도 함께 삭제되어 재평가가 필요합니다)`
                    : '')
                + '. 이 작업은 되돌릴 수 없습니다. 실행하시겠습니까?';
            const ok = await this.$bvModal.msgBoxConfirm(message, {
                title: '재배정 실행 확인',
                okVariant: 'danger',
                okTitle: '재배정 실행',
                cancelTitle: '취소',
                centered: true,
            });
            if (!ok) return;

            this.executing = true;
            try {
                // ⛔ execute_requires 를 그대로 전달 — 화면에서 재계산 금지.
                this.result = await executeRemap({
                    companyId: this.selectedCompany.company_id,
                    fromCampaignId: this.selectedCompany.campaign_id,
                    toCampaignId: this.toCampaignId,
                    executeRequires: p.execute_requires,
                });
                this.preview = null;
                this.$EventBus.$emit('toast-success', '재배정 완료',
                    `${this.selectedCompany.text} 의 캠페인을 변경했습니다.`);
                this.$emit('executed');
                // 이동 후에는 업체의 현재 캠페인이 바뀌었으므로 목록을 다시 읽는다.
                this.onProjectChange();
            } catch (e) {
                this.$EventBus.$emit(
                    'toast-failure', '재배정 실패',
                    remapErrorMessage(e, '재배정에 실패했습니다.'),
                );
            } finally {
                this.executing = false;
            }
        },
    },
};
</script>

<style scoped>
.summary-box {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 12px 8px;
}

.summary-label {
    font-size: 0.8rem;
    color: #6c757d;
}

.summary-value {
    font-size: 1.5rem;
    font-weight: 600;
}

.delete-list {
    max-height: 220px;
    overflow-y: auto;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
}
</style>
